/**
 * Anonymous Key Roster Page
 * Per-creator CRUD list mapping anonymous visitor keys to human-readable display names.
 * Names are private to the creator and only relabel their own analytics views.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Form, Input, Modal, Popconfirm, Space, Table, Typography, Upload, message } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  IdcardOutlined,
  ImportOutlined,
  InboxOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import PagesWrapper from '../../PagesWrapper';
import {
  createAnonymousKeyAlias,
  deleteAnonymousKeyAlias,
  getAnonymousKeyAliases,
  importAnonymousKeyAliases,
  updateAnonymousKeyAlias,
} from '../../services/learningPackagesService';
import styles from './LearningPackages.module.css';

const { Search } = Input;
const { Text, Title } = Typography;

/**
 * Renders the creator's private roster of anonymous-key display names.
 * @returns {JSX.Element} Roster page
 */
const AnonymousRosterPage = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAlias, setEditingAlias] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importEntries, setImportEntries] = useState([]);
  const [importParseError, setImportParseError] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);

  const loadAliases = useCallback(async (page = 1, searchTerm = '', pageSize = pagination.pageSize) => {
    setLoading(true);
    try {
      const result = await getAnonymousKeyAliases({
        page,
        pageSize,
        search: searchTerm || undefined,
      });
      setItems(result.items || []);
      setPagination(prev => ({
        ...prev,
        current: result.page || page,
        pageSize: result.pageSize || pageSize,
        total: result.totalCount || 0,
      }));
    } catch (error) {
      message.error(t('learningPackages.rosterFetchError', 'Failed to load roster'));
    } finally {
      setLoading(false);
    }
  }, [pagination.pageSize, t]);

  useEffect(() => {
    loadAliases();
  }, [loadAliases]);

  const handleSearch = (value) => {
    setSearch(value);
    loadAliases(1, value, pagination.pageSize);
  };

  const handleTableChange = (paginationConfig) => {
    loadAliases(paginationConfig.current, search, paginationConfig.pageSize);
  };

  const openCreateModal = () => {
    setEditingAlias(null);
    form.setFieldsValue({ anonymousVisitorKey: '', displayName: '', note: '' });
    setModalOpen(true);
  };

  const openEditModal = (alias) => {
    setEditingAlias(alias);
    form.setFieldsValue({
      anonymousVisitorKey: alias.anonymousVisitorKey,
      displayName: alias.displayName,
      note: alias.note || '',
    });
    setModalOpen(true);
  };

  /**
   * Normalizes one parsed row (JSON object or CSV columns) to an import entry.
   * Accepts flexible field names: anonymousVisitorKey/visitorKey/key, displayName/name, note.
   */
  const normalizeImportRow = (row) => {
    const key = (row.anonymousVisitorKey || row.visitorKey || row.key || '').toString().trim();
    const displayName = (row.displayName || row.name || '').toString().trim();
    const note = (row.note || '').toString().trim();
    if (!key || !displayName) return null;
    return { anonymousVisitorKey: key, displayName, note: note || undefined };
  };

  /**
   * Minimal CSV parser supporting quoted fields. Detects a header row by
   * key/name keywords; otherwise columns are positional: key, name, note.
   */
  const parseCsv = (text) => {
    const parseLine = (line) => {
      const cells = [];
      let current = '';
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (inQuotes) {
          if (char === '"' && line[i + 1] === '"') {
            current += '"';
            i++;
          } else if (char === '"') {
            inQuotes = false;
          } else {
            current += char;
          }
        } else if (char === '"') {
          inQuotes = true;
        } else if (char === ',' || char === ';') {
          cells.push(current);
          current = '';
        } else {
          current += char;
        }
      }
      cells.push(current);
      return cells.map(cell => cell.trim());
    };

    const lines = text.split(/\r?\n/).filter(line => line.trim().length > 0);
    if (lines.length === 0) return [];

    const firstCells = parseLine(lines[0]).map(cell => cell.toLowerCase());
    const hasHeader = firstCells.some(cell => ['anonymousvisitorkey', 'visitorkey', 'key', 'displayname', 'name', 'note'].includes(cell));

    let keyIndex = 0;
    let nameIndex = 1;
    let noteIndex = 2;
    let dataLines = lines;
    if (hasHeader) {
      keyIndex = firstCells.findIndex(cell => ['anonymousvisitorkey', 'visitorkey', 'key'].includes(cell));
      nameIndex = firstCells.findIndex(cell => ['displayname', 'name'].includes(cell));
      noteIndex = firstCells.findIndex(cell => cell === 'note');
      dataLines = lines.slice(1);
    }

    return dataLines
      .map(line => {
        const cells = parseLine(line);
        return normalizeImportRow({
          key: keyIndex >= 0 ? cells[keyIndex] : undefined,
          name: nameIndex >= 0 ? cells[nameIndex] : undefined,
          note: noteIndex >= 0 ? cells[noteIndex] : undefined,
        });
      })
      .filter(Boolean);
  };

  const handleImportFile = (file) => {
    setImportParseError(null);
    setImportResult(null);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = String(reader.result || '');
        let entries;
        if (file.name.toLowerCase().endsWith('.json') || text.trimStart().startsWith('[') || text.trimStart().startsWith('{')) {
          const parsed = JSON.parse(text);
          const rows = Array.isArray(parsed) ? parsed : (Array.isArray(parsed.entries) ? parsed.entries : []);
          entries = rows.map(normalizeImportRow).filter(Boolean);
        } else {
          entries = parseCsv(text);
        }
        if (entries.length === 0) {
          setImportEntries([]);
          setImportParseError(t('learningPackages.importNoEntries', 'No valid entries found in the file'));
        } else {
          setImportEntries(entries.slice(0, 500));
        }
      } catch (error) {
        setImportEntries([]);
        setImportParseError(t('learningPackages.importParseError', 'Could not parse the file. Use JSON or CSV with key, name, and optional note columns.'));
      }
    };
    reader.readAsText(file);
    return false; // prevent antd upload
  };

  const handleImport = async () => {
    if (importEntries.length === 0) return;
    setImporting(true);
    try {
      const result = await importAnonymousKeyAliases(importEntries, { suppressNotification: true });
      setImportResult(result);
      setImportEntries([]);
      loadAliases(1, search, pagination.pageSize);
    } catch (error) {
      message.error(t('learningPackages.importError', 'Import failed'));
    } finally {
      setImporting(false);
    }
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (editingAlias) {
        await updateAnonymousKeyAlias(editingAlias.id, {
          displayName: values.displayName,
          note: values.note || undefined,
          clearNote: !values.note,
        }, { successMessage: t('learningPackages.rosterSaved', 'Roster entry saved') });
      } else {
        await createAnonymousKeyAlias({
          anonymousVisitorKey: values.anonymousVisitorKey,
          displayName: values.displayName,
          note: values.note || undefined,
        }, { successMessage: t('learningPackages.rosterSaved', 'Roster entry saved') });
      }
      setModalOpen(false);
      loadAliases(pagination.current, search, pagination.pageSize);
    } catch (error) {
      message.error(t('learningPackages.rosterError', 'Failed to save roster entry'));
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: t('learningPackages.rosterName', 'Display Name'),
      dataIndex: 'displayName',
      key: 'displayName',
      sorter: (a, b) => a.displayName.localeCompare(b.displayName),
    },
    {
      title: t('learningPackages.rosterKey', 'Anonymous Key'),
      dataIndex: 'anonymousVisitorKey',
      key: 'anonymousVisitorKey',
      render: (key) => <span className={styles.anonymousKeyCell}>{key}</span>,
    },
    {
      title: t('learningPackages.rosterNote', 'Note'),
      dataIndex: 'note',
      key: 'note',
      render: (note) => note || '—',
    },
    {
      title: t('learningPackages.columnActions', 'Actions'),
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button type="text" icon={<EditOutlined />} onClick={() => openEditModal(record)} />
          <Popconfirm
            title={t('learningPackages.rosterDeleteConfirm', 'Delete this roster entry?')}
            okButtonProps={{ danger: true }}
            onConfirm={async () => {
              await deleteAnonymousKeyAlias(record.id, { successMessage: t('learningPackages.rosterDeleted', 'Roster entry deleted') });
              loadAliases(pagination.current, search, pagination.pageSize);
            }}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <PagesWrapper>
      <div className={styles.rosterContainer}>
        <div className={styles.listHeader}>
          <div>
            <div className={styles.titleSection}>
              <IdcardOutlined className={styles.titleIcon} />
              <Title level={2} className={styles.title}>{t('learningPackages.rosterTitle', 'Visitor Name Roster')}</Title>
            </div>
            <Text type="secondary" className={styles.listDescription}>
              {t('learningPackages.rosterSubtitle', 'Your private list of display names for anonymous visitor keys. Names appear only in your own analytics views.')}
            </Text>
          </div>
        </div>

        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('learningPackages.anonymousNotice', 'Your progress is tracked anonymously in this browser. It does not require an account and is not a login.')}
        />

        <div className={styles.toolbar}>
          <Space>
            <Search
              placeholder={t('learningPackages.rosterSearchPlaceholder', 'Search by name, key, or note...')}
              onSearch={handleSearch}
              allowClear
              style={{ width: 320 }}
            />
            <Button icon={<ReloadOutlined />} onClick={() => loadAliases(pagination.current, search, pagination.pageSize)}>
              {t('common.refresh', 'Refresh')}
            </Button>
          </Space>
          <Space>
            <Button
              icon={<ImportOutlined />}
              onClick={() => {
                setImportEntries([]);
                setImportParseError(null);
                setImportResult(null);
                setImportModalOpen(true);
              }}
            >
              {t('learningPackages.importButton', 'Import (JSON/CSV)')}
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
              {t('learningPackages.rosterAdd', 'Add Entry')}
            </Button>
          </Space>
        </div>

        <Table
          rowKey="id"
          loading={loading}
          dataSource={items}
          columns={columns}
          pagination={{
            ...pagination,
            showSizeChanger: false,
          }}
          onChange={handleTableChange}
          locale={{
            emptyText: <div className={styles.emptyState}>{t('learningPackages.rosterEmpty', 'No roster entries yet')}</div>,
          }}
        />

        <Modal
          title={editingAlias
            ? t('learningPackages.rosterName', 'Display Name')
            : t('learningPackages.rosterAdd', 'Add Entry')}
          open={modalOpen}
          onCancel={() => setModalOpen(false)}
          onOk={handleSave}
          confirmLoading={saving}
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="anonymousVisitorKey"
              label={t('learningPackages.rosterKey', 'Anonymous Key')}
              rules={[{ required: true, message: t('learningPackages.rosterKey', 'Anonymous Key') }]}
            >
              <Input maxLength={100} disabled={!!editingAlias} />
            </Form.Item>
            <Form.Item
              name="displayName"
              label={t('learningPackages.rosterName', 'Display Name')}
              rules={[{ required: true, message: t('learningPackages.nameRequired', 'Name is required') }]}
            >
              <Input maxLength={100} />
            </Form.Item>
            <Form.Item name="note" label={t('learningPackages.rosterNote', 'Note')}>
              <Input.TextArea maxLength={500} rows={2} />
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title={t('learningPackages.importTitle', 'Import roster entries')}
          open={importModalOpen}
          onCancel={() => setImportModalOpen(false)}
          onOk={handleImport}
          okText={t('learningPackages.importConfirm', 'Import')}
          okButtonProps={{ disabled: importEntries.length === 0 }}
          confirmLoading={importing}
        >
          <Typography.Paragraph type="secondary">
            {t('learningPackages.importHint', 'Upload a JSON array or CSV file. Columns: anonymousVisitorKey (or key), displayName (or name), note (optional). Existing keys are updated.')}
          </Typography.Paragraph>
          <Upload.Dragger
            accept=".json,.csv,.txt"
            maxCount={1}
            beforeUpload={handleImportFile}
            showUploadList={false}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">{t('learningPackages.importDropHint', 'Click or drag a JSON/CSV file here')}</p>
          </Upload.Dragger>
          {importParseError && (
            <Alert type="error" showIcon style={{ marginTop: 12 }} message={importParseError} />
          )}
          {importEntries.length > 0 && (
            <Alert
              type="success"
              showIcon
              style={{ marginTop: 12 }}
              message={t('learningPackages.importReady', {
                count: importEntries.length,
                defaultValue: '{{count}} entries ready to import',
              })}
            />
          )}
          {importResult && (
            <Alert
              type="info"
              showIcon
              style={{ marginTop: 12 }}
              message={t('learningPackages.importResult', {
                created: importResult.created,
                updated: importResult.updated,
                skipped: importResult.skipped,
                defaultValue: 'Import finished: {{created}} created, {{updated}} updated, {{skipped}} skipped',
              })}
              description={(importResult.errors || []).length > 0 ? (
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {importResult.errors.slice(0, 10).map((error, index) => <li key={index}>{error}</li>)}
                </ul>
              ) : null}
            />
          )}
        </Modal>
      </div>
    </PagesWrapper>
  );
};

export default AnonymousRosterPage;
